// ========================================
// KEY INFO MODAL
// Read-only view of all details for a LiteLLM virtual key
// ========================================

import React from 'react';
import Modal from './shared/Modal';
import Button from './shared/Button';
import StatusBadge from './shared/StatusBadge';

const fieldStyle = {
    marginBottom: '0.75rem'
};

const labelStyle = {
    fontSize: '0.78rem',
    color: 'var(--muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '0.2rem'
};

const valueStyle = {
    color: 'var(--fg-primary)',
    fontSize: '0.9rem',
    wordBreak: 'break-all'
};

const monoStyle = {
    ...valueStyle,
    fontFamily: 'monospace',
    color: 'var(--fg-secondary)'
};

const sectionStyle = {
    backgroundColor: 'var(--surface)',
    borderRadius: '8px',
    padding: '1rem',
    marginBottom: '1rem'
};

const sectionTitleStyle = {
    fontSize: '0.72rem',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    color: 'var(--muted)',
    fontWeight: '700',
    marginBottom: '0.75rem'
};

const gridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem'
};

const InfoField = ({ label, value, mono = false, children }) => (
    <div style={fieldStyle}>
        <div style={labelStyle}>{label}</div>
        {children || (
            <div style={mono ? monoStyle : valueStyle}>
                {value != null && value !== '' ? String(value) : <em style={{ color: 'var(--muted)' }}>None</em>}
            </div>
        )}
    </div>
);

const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
        return new Date(dateStr).toLocaleString('en-GB', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit'
        });
    } catch {
        return dateStr;
    }
};

const formatNumber = (n) => {
    if (n == null) return null;
    return Number(n).toLocaleString();
};

/**
 * KeyInfoModal - read-only full detail view
 *
 * @param {Object} keyData - Full key object from LiteLLM (return_full_object=true)
 * @param {Function} onClose
 * @param {Array} allTeams - All teams for resolving team_id to team name
 * @param {Array} allUsers - All users for resolving user_id to email
 */
const KeyInfoModal = ({ keyData, onClose, allTeams = [], allUsers = [] }) => {
    if (!keyData) return null;

    // Helper function to resolve team ID to team name
    const resolveTeamName = (teamId) => {
        if (!teamId) return null;
        const team = allTeams.find(t => t.team_id === teamId);
        return team?.team_alias || team?.team_id || teamId;
    };

    // Helper function to resolve user ID to email
    // allUsers contains LiteLLM internal users with fields: user_id, user_email
    const resolveUserEmail = (userId) => {
        if (!userId) return null;
        // LiteLLM users have user_id and user_email fields
        const user = allUsers.find(u =>
            u.user_id === userId ||
            u.id === userId ||
            u._id === userId
        );
        return user?.user_email || user?.email || userId;
    };

    const {
        key_name,
        key_alias,
        spend,
        max_budget,
        budget_duration,
        budget_reset_at,
        expires,
        models,
        blocked,
        team_id,
        user_id,
        tpm_limit,
        rpm_limit,
        max_parallel_requests,
        metadata,
        tags,
        created_at,
        updated_at,
        created_by,
        updated_by,
        soft_budget_cooldown,
        model_spend,
        allowed_routes,
        allowed_cache_controls,
        permissions
    } = keyData;

    const hasSpendByModel = model_spend && Object.keys(model_spend).length > 0;
    const hasMetadata = metadata && Object.keys(metadata).length > 0;
    const hasTags = tags && tags.length > 0;
    const hasRoutes = allowed_routes && allowed_routes.length > 0;

    // Build budget display
    const budgetLine = max_budget != null && max_budget > 0
        ? `$${(spend || 0).toFixed(2)} of $${max_budget.toFixed(2)} (${((spend / max_budget) * 100).toFixed(1)}% used)`
        : `$${(spend || 0).toFixed(2)} (unlimited)`;

    return (
        <Modal
            isOpen={true}
            onClose={onClose}
            title="Key Details"
            maxWidth="580px"
            zIndex={1200}
            footer={
                <Button variant="secondary" onClick={onClose}>Close</Button>
            }
        >
            {/* Identity */}
            <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Identity</div>
                <InfoField label="Alias">
                    <div style={valueStyle}>
                        {key_alias || <em style={{ color: 'var(--muted)' }}>Unnamed</em>}
                    </div>
                </InfoField>
                <InfoField label="Key (masked)" value={`sk-...${(key_name || '').slice(-4)}`} mono />
                <InfoField label="Status">
                    <StatusBadge
                        label={blocked ? 'Blocked' : 'Active'}
                        variant={blocked ? 'unhealthy' : 'healthy'}
                        size="small"
                    />
                </InfoField>
            </div>

            {/* Budget & Spend */}
            <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Budget & Spend (Key-Level)</div>
                <InfoField label="Current Spend">
                    <div style={{
                        ...valueStyle,
                        color: (max_budget > 0 && spend >= max_budget) ? 'var(--critical)' : 'var(--success)'
                    }}>
                        {budgetLine}
                    </div>
                    {max_budget > 0 && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                            This is the budget limit for this specific key, independent of team budget.
                        </div>
                    )}
                </InfoField>
                <div style={gridStyle}>
                    <InfoField
                        label="Budget Resets"
                        value={budget_duration || 'Never'}
                    />
                    <InfoField
                        label="Next Reset"
                        value={budget_reset_at ? formatDate(budget_reset_at) : 'N/A'}
                    />
                </div>
                {soft_budget_cooldown && (
                    <InfoField label="Soft Budget Cooldown" value="Active" />
                )}
            </div>

            {/* Limits */}
            <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Rate Limits</div>
                <div style={gridStyle}>
                    <InfoField
                        label="Requests per Minute (RPM)"
                        value={formatNumber(rpm_limit) || 'Unlimited'}
                    />
                    <InfoField
                        label="Tokens per Minute (TPM)"
                        value={formatNumber(tpm_limit) || 'Unlimited'}
                    />
                    <InfoField
                        label="Max Parallel Requests"
                        value={formatNumber(max_parallel_requests) || 'Unlimited'}
                    />
                    <InfoField
                        label="Expires"
                        value={expires ? formatDate(expires) : 'Never'}
                    />
                </div>
            </div>

            {/* Models */}
            <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Allowed Models (Key-Level)</div>
                {models && models.length > 0 ? (
                    <>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                            {models.map(m => (
                                <span key={m} style={{
                                    padding: '0.25rem 0.6rem',
                                    backgroundColor: 'var(--surface)',
                                    borderRadius: '4px',
                                    fontSize: '0.8rem',
                                    fontFamily: 'monospace',
                                    color: 'var(--fg-secondary)',
                                    border: '1px solid var(--border)'
                                }}>
                                    {m}
                                </span>
                            ))}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
                            This key can only access these {models.length} model{models.length > 1 ? 's' : ''}, regardless of team model access.
                        </div>
                    </>
                ) : (
                    <>
                        <div style={{ color: 'var(--success)', fontSize: '0.9rem' }}>All team models</div>
                        <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.3rem' }}>
                            This key can access any model the team has access to.
                        </div>
                    </>
                )}
            </div>

            {/* Spend by model */}
            {hasSpendByModel && (
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Spend by Model</div>
                    {Object.entries(model_spend).map(([model, spent]) => (
                        <div key={model} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            padding: '0.3rem 0',
                            borderBottom: '1px solid var(--border)',
                            fontSize: '0.85rem'
                        }}>
                            <span style={{ fontFamily: 'monospace', color: 'var(--fg-secondary)' }}>{model}</span>
                            <span style={{ color: 'var(--fg-primary)' }}>${Number(spent).toFixed(4)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Associated Team and User */}
            <div style={sectionStyle}>
                <div style={sectionTitleStyle}>Associated Team and User</div>
                <div style={gridStyle}>
                    <InfoField label="Associated Team">
                        <div style={valueStyle}>{resolveTeamName(team_id) || <em style={{ color: 'var(--muted)' }}>None</em>}</div>
                        {team_id && resolveTeamName(team_id) !== team_id && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                                ID: {team_id}
                            </div>
                        )}
                    </InfoField>
                    <InfoField label="User">
                        <div style={valueStyle}>{resolveUserEmail(user_id) || <em style={{ color: 'var(--muted)' }}>None</em>}</div>
                        {user_id && resolveUserEmail(user_id) !== user_id && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                                ID: {user_id}
                            </div>
                        )}
                    </InfoField>
                </div>
            </div>

            {/* Tags */}
            {hasTags && (
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Tags</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {tags.map(tag => (
                            <span key={tag} style={{
                                padding: '0.2rem 0.6rem',
                                backgroundColor: 'var(--surface)',
                                border: '1px solid var(--border)',
                                borderRadius: '12px',
                                fontSize: '0.8rem',
                                color: 'var(--fg-primary)'
                            }}>
                                {tag}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Metadata */}
            {hasMetadata && (
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Metadata</div>
                    {Object.entries(metadata).map(([k, v]) => (
                        <div key={k} style={{
                            display: 'flex',
                            gap: '0.5rem',
                            padding: '0.3rem 0',
                            borderBottom: '1px solid var(--border)',
                            fontSize: '0.85rem'
                        }}>
                            <span style={{ color: 'var(--border)', fontWeight: '500', minWidth: '120px' }}>{k}</span>
                            <span style={{ color: 'var(--fg-primary)' }}>{String(v)}</span>
                        </div>
                    ))}
                </div>
            )}

            {/* Allowed Routes */}
            {hasRoutes && (
                <div style={sectionStyle}>
                    <div style={sectionTitleStyle}>Allowed Routes</div>
                    {allowed_routes.map(route => (
                        <div key={route} style={{
                            fontFamily: 'monospace',
                            fontSize: '0.82rem',
                            color: 'var(--fg-secondary)',
                            padding: '0.2rem 0'
                        }}>
                            {route}
                        </div>
                    ))}
                </div>
            )}

            {/* Timestamps */}
            <div style={{ ...sectionStyle, marginBottom: 0 }}>
                <div style={sectionTitleStyle}>Audit</div>
                <div style={gridStyle}>
                    <InfoField label="Created" value={formatDate(created_at)} />
                    <InfoField label="Created By">
                        <div style={valueStyle}>{resolveUserEmail(created_by)}</div>
                        {created_by && resolveUserEmail(created_by) !== created_by && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                                ID: {created_by}
                            </div>
                        )}
                    </InfoField>
                    <InfoField label="Last Updated" value={formatDate(updated_at)} />
                    <InfoField label="Updated By">
                        <div style={valueStyle}>{resolveUserEmail(updated_by)}</div>
                        {updated_by && resolveUserEmail(updated_by) !== updated_by && (
                            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'monospace', marginTop: '0.2rem' }}>
                                ID: {updated_by}
                            </div>
                        )}
                    </InfoField>
                </div>
            </div>
        </Modal>
    );
};

export default KeyInfoModal;

